import React from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';

import styles from './style.module.scss';
import Button from '@components-clean/core/button';

type InfoFieldProps = {
    message: React.ReactNode;
    type: 'Info' | 'Warning' | 'Error';
    layout?: 'stretch' | 'center' | 'cardOverlay';
    truncated?: boolean;
    showButton?: boolean;
    buttonLabel?: string;
    buttonCallback?: () => void;
};

export const InfoField = ({
    message,
    type,
    layout = 'stretch',
    truncated,
    showButton,
    buttonLabel,
    buttonCallback,
}: InfoFieldProps) => {
    const wrapperClasses = cn('info-field', `type-${type.toLocaleLowerCase()}`, styles.infoFieldWrapper, {
        [styles.infoType]: type === 'Info',
        [styles.warningType]: type === 'Warning',
        [styles.errorType]: type === 'Error',
    });

    const messageClasses = cn(styles.message, {
        [styles.wrapMessage]: !truncated,
    });

    const getIcon = (): IconProp => {
        switch (type) {
            case 'Info':
                return ['fal', 'circle-exclamation'];
            case 'Warning':
                return ['fal', 'triangle-exclamation'];
            case 'Error':
            default:
                return ['fal', 'square-exclamation'];
        }
    };

    const content = (
        <div className={wrapperClasses}>
            <FontAwesomeIcon
                data-testid="info-field-icon"
                style={type.match('Info') ? { transform: 'rotate(180deg)' } : undefined}
                fontSize="20px"
                icon={getIcon()}
                height="18px"
                width="20px"
            />

            <div className={messageClasses}>{message}</div>
            {showButton && (
                <Button data-testid="primary-button" primary onClick={buttonCallback}>
                    {buttonLabel}
                </Button>
            )}
        </div>
    );

    return layout === 'stretch' ? (
        content
    ) : layout === 'center' ? (
        <div className="k-d-grid k-align-content-center k-justify-content-center k-h-full">{content}</div>
    ) : (
        <div
            className={cn(
                'k-d-grid k-align-content-center k-justify-content-center k-h-full k-w-full',
                styles.cardOverlay,
            )}
        >
            {content}
        </div>
    );
};

export const FullPageInfoField = ({ message, type }: Omit<InfoFieldProps, 'layout' | 'truncated'>) => (
    <div className="opra-default-theme full-content-height-with-scroll">
        <InfoField message={message} type={type} layout="center" />
    </div>
);
